import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../../../../../common/global/helpers';
import Icons from '../../../../../common/widgets/Icons';
import PopoverDialog from '../../../../../common/widgets/PopoverDialog';
import { config } from '../../../config';
import { paymentMethodCodes } from '../../../constants';
import { BudgetTypes } from '../../../enums';
import { PaymentMethodModel } from '../../../models/PaymentMethods';
import { ProductPaymentMethodDetailsModel } from '../../../models/ProductPaymentMethodDetailsModel';
import { resources } from '../../../resources/resources';
import {
  getBudgetTaxesMessage,
  getSalaryTaxesMessage,
} from '../../CartPage/Components/helper';
import { ApplyCashbackDialog } from './Dialogs/ApplyCashbackDialog';
import { Budget } from '../../../contexts/common';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';

interface ProductPaymentMethodsProps {
  productPaymentMethods: ProductPaymentMethodDetailsModel[];
  selectedCheckoutMethod: BudgetTypes | undefined;
  setSelectedCheckoutMethod: (budgetType: BudgetTypes) => void;
  setIsSalaryDeductionSelected: (isSalaryDeductionSelected: boolean) => void;
  isRecurring: boolean;
  setPaymentError: (value: string | undefined) => void;
  hasPriceRange: boolean;
  paymentMethods?: PaymentMethodModel[];
  customerBudget: Budget;
  cashbackDiscount: number;
  productPrice: number;
  setCashbackDiscount?: (value: number) => void;
  isPayableByCashback: boolean;
}

const sumArray = (arr: number[]): number => {
  return arr.reduce((sum, val) => sum + val, 0);
};

const formatTaxes = (taxes: number[], currency: string): string => {
  if (taxes.length > 1) {
    return taxes.map(tax => `${formatPrice(tax)} ${currency}`).join(' + ');
  }
  return `${formatPrice(taxes[0])} ${currency}`;
};

interface TaxSummary {
  budgetTaxes: number;
  salaryTaxes: number;
}

const isGrossup = (taxes: TaxSummary): boolean => {
  return taxes.budgetTaxes > 0 && taxes.salaryTaxes === 0;
};

type PaymentCategoryUi = {
  title: React.ReactNode;
  ariaTitle: string;
  taxesInfoComponent: React.ReactNode;
};

export const ProductPaymentMethods = (props: ProductPaymentMethodsProps) => {
  const {
    productPaymentMethods,
    selectedCheckoutMethod,
    setSelectedCheckoutMethod,
    setIsSalaryDeductionSelected,
    isRecurring,
    setPaymentError,
    paymentMethods,
    customerBudget,
    productPrice,
  } = props;
  const { t: translate } = useTranslation();
  const [showApplyCashbackDialog, setShowApplyCashbackDialog] = useState(false);
  const { customerInfo } = useContext(CustomerInfoContext);
  const isSinglePaymentMethod = productPaymentMethods.length === 1;

  const recurringSuffix = isRecurring
    ? `/${translate(resources.cart.summary.month)}`
    : '';

  const formatTotal = (amount: number): string => {
    return `${formatPrice(amount)} ${config.currency}${recurringSuffix}`;
  };

  useEffect(() => {
    if (productPaymentMethods.length > 0) {
      // Check if selectedCheckoutMethod is still valid
      const isSelectedMethodValid =
        selectedCheckoutMethod !== undefined &&
        productPaymentMethods.some(
          item => item.budgetType === selectedCheckoutMethod,
        );

      // Only set default if nothing is selected or current selection is no longer valid
      if (selectedCheckoutMethod === undefined || !isSelectedMethodValid) {
        const defaultMethod = productPaymentMethods.find(
          item => item.default === true,
        );

        if (defaultMethod) {
          setSelectedCheckoutMethod(defaultMethod.budgetType);
          setIsSalaryDeductionSelected(defaultMethod.isSalaryDeduction);
        } else if (productPaymentMethods.length === 1) {
          // Fallback: if no default is set but only one method exists
          setSelectedCheckoutMethod(productPaymentMethods[0].budgetType);
          setIsSalaryDeductionSelected(
            productPaymentMethods[0].isSalaryDeduction,
          );
        }
      }

      const selectedMethod = productPaymentMethods.find(
        item => item.budgetType === selectedCheckoutMethod,
      );

      const isPaymentError =
        selectedCheckoutMethod === BudgetTypes.BenefitBudgetOnly &&
        isRecurring &&
        !!selectedMethod?.error;

      if (isPaymentError) {
        setPaymentError(selectedMethod.notificationError);
      } else {
        setPaymentError(undefined);
      }
    }
  }, [
    productPaymentMethods,
    selectedCheckoutMethod,
    setSelectedCheckoutMethod,
    setIsSalaryDeductionSelected,
    isRecurring,
    setPaymentError,
  ]);

  const renderTaxInfo = (
    paymentCategory: ProductPaymentMethodDetailsModel,
    showBudgetTaxes: boolean = true,
  ) => {
    const totalSalaryTaxes = sumArray(paymentCategory.salaryTaxes);

    const hasMultipleBudgetTaxes = paymentCategory.budgetTaxes.length > 1;

    const remainingBudgetTaxes = hasMultipleBudgetTaxes
      ? paymentCategory.budgetTaxes.slice(1)
      : paymentCategory.budgetTaxes;
    const totalBudgetTaxes = sumArray(remainingBudgetTaxes);

    const taxSummary: TaxSummary = {
      budgetTaxes: totalBudgetTaxes,
      salaryTaxes: totalSalaryTaxes,
    };

    return (
      <>
        {showBudgetTaxes && customerInfo.platformType === 1 && (
          <div className="summary-label">
            <span className="payment-info">
              {`${formatTaxes(
                paymentCategory.budgetTaxes,
                config.currency,
              )} ${translate(resources.cart.summary.budgetTaxes.label)} `}
            </span>
            <PopoverDialog
              className="checkout-info-dropdown"
              placement="right"
              closeFromContent
              type="hover"
              content={
                <span>
                  {translate(
                    getBudgetTaxesMessage(
                      totalBudgetTaxes,
                      isGrossup(taxSummary),
                    ),
                  )}
                </span>
              }
            >
              <button className="product-payment cart-info-btn" type="button">
                <span className="btn-icon">
                  <Icons.InfoSummary />
                </span>
              </button>
            </PopoverDialog>
          </div>
        )}
        {customerInfo.platformType === 1 && (
          <div>
            <span className="payment-info">
              {formatTaxes(paymentCategory.salaryTaxes, config.currency)}{' '}
              {translate(resources.cart.summary.salaryTaxes.label)}{' '}
            </span>
            <PopoverDialog
              className="info-tooltip"
              placement="right"
              closeFromContent
              type="hover"
              content={
                <span>
                  {translate(
                    getSalaryTaxesMessage(
                      totalSalaryTaxes,
                      totalBudgetTaxes > 0,
                    ),
                  )}
                </span>
              }
            >
              <button className="product-payment cart-info-btn" type="button">
                <span className="btn-icon">
                  <Icons.InfoSummary />
                </span>
              </button>
            </PopoverDialog>
          </div>
        )}
      </>
    );
  };

  const paymentCategoriesUi = (
    paymentCategory: ProductPaymentMethodDetailsModel,
  ): PaymentCategoryUi => {
    const hasMultipleTotals =
      paymentCategory?.totals && paymentCategory.totals.length > 1;

    const hidePrice = props.hasPriceRange && paymentCategory.totals[0] <= 0;

    const formatTitle = (): {
      title: React.ReactNode;
      ariaTitle: string;
    } => {
      const benefitLabel = translate(
        resources.products.paymentMethod.benefitOnly,
      );
      const copaymentLabel = translate(resources.products.paymentMethod.card);
      const salaryDeductionLabel = translate(
        resources.products.paymentMethod.salaryDeduction,
      );

      const firstTotal = hidePrice
        ? ''
        : formatTotal(paymentCategory.totals[0]);

      if (hasMultipleTotals) {
        const secondTotal = formatTotal(paymentCategory.totals[1]);

        const secondLabel = paymentCategory.isSalaryDeduction
          ? salaryDeductionLabel
          : copaymentLabel;

        return {
          title: (
            <div>
              {benefitLabel}: {firstTotal} + {secondLabel}: {secondTotal}
            </div>
          ),
          ariaTitle: `${benefitLabel}: ${firstTotal} + ${secondLabel}: ${secondTotal}`,
        };
      }

      return {
        title: (
          <div>
            {benefitLabel}
            {hidePrice ? '' : ':'} {firstTotal}
          </div>
        ),
        ariaTitle: `${benefitLabel}: ${firstTotal}`,
      };
    };

    switch (paymentCategory.budgetType) {
      case BudgetTypes.BenefitBudgetOnly:
      case BudgetTypes.BenefitAndCoPayment: {
        const formattedTitle = formatTitle();

        return {
          title: formattedTitle.title,
          ariaTitle: formattedTitle.ariaTitle,
          taxesInfoComponent: renderTaxInfo(paymentCategory, true),
        };
      }

      case BudgetTypes.CoPaymentOnly: {
        const label = (() => {
          if (paymentCategory.isSalaryDeduction) {
            return translate(resources.products.paymentMethod.salaryDeduction);
          }

          const hasMobilpay = paymentMethods?.some(
            pm => pm.code === paymentMethodCodes.mobilpay,
          );
          const hasMobilpayGv = paymentMethods?.some(
            pm => pm.code === paymentMethodCodes.mobilpaygv,
          );
          const hasMobilpayCv = paymentMethods?.some(
            pm => pm.code === paymentMethodCodes.mobilpaycv,
          );

          // Card + Gift Card + Cultural Card
          if (hasMobilpay && hasMobilpayGv && hasMobilpayCv) {
            return translate(
              resources.products.paymentMethod.copaymentOnlyAndCvAndGv,
            );
          }

          // Card + Gift Card
          if (hasMobilpay && hasMobilpayGv) {
            return translate(
              resources.products.paymentMethod.copaymentOnlyAndGv,
            );
          }

          // Card + Cultural Card
          if (hasMobilpay && hasMobilpayCv) {
            return translate(
              resources.products.paymentMethod.copaymentOnlyAndCv,
            );
          }

          // Gift Card
          if (hasMobilpayGv) {
            return translate(resources.products.paymentMethod.copaymentOnlyGv);
          }

          // Cultural Card
          if (hasMobilpayCv) {
            return translate(resources.products.paymentMethod.copaymentOnlyCv);
          }

          // Card
          return translate(resources.products.paymentMethod.copaymentOnly);
        })();

        const total = hidePrice ? '' : formatTotal(paymentCategory.totals[0]);

        return {
          title: (
            <div>
              {label}
              {hidePrice ? '' : ':'} {total}
            </div>
          ),
          ariaTitle: hidePrice ? label : `${label}: ${total}`,
          taxesInfoComponent: renderTaxInfo(paymentCategory, false),
        };
      }
      default:
        throw new Error('Payment category not supported');
    }
  };

  return (
    <div className="product-payment-methods">
      <ul className="checkout-payments-options list-reset">
        {props.isPayableByCashback && !!customerBudget.benefitBucksBalance && (
          <li>
            <div
              className={`cashback-row ${
                props.cashbackDiscount ? 'edit-cashback' : 'apply-cashback'
              }`}
            >
              <div className="cashback-left ">
                <span>
                  {!props.cashbackDiscount && <Icons.CashbackToApply />}
                  {!!props.cashbackDiscount && <Icons.CashbackApplyed />}
                </span>
                {!props.cashbackDiscount && (
                  <div>
                    <label>
                      {translate(
                        resources.products.paymentMethod.cashbackToUse,
                      )}
                    </label>
                    <br />
                    <label>
                      <b>
                        {customerBudget.benefitBucksBalance}
                        {config.currency}
                      </b>
                    </label>
                  </div>
                )}
                {!!props.cashbackDiscount && (
                  <div className="horizontal">
                    <label>
                      {translate(resources.products.paymentMethod.cashbackUsed)}
                    </label>
                    <br />
                    <label>
                      <b>
                        {props.cashbackDiscount}
                        {config.currency}
                      </b>
                    </label>
                  </div>
                )}
              </div>
              <button
                className={`btn btn-outline ${
                  props.cashbackDiscount
                    ? 'btn-edit-cashback'
                    : 'btn-apply-cashback'
                }`}
                type="button"
                onClick={() => setShowApplyCashbackDialog(true)}
              >
                {props.cashbackDiscount
                  ? translate(resources.products.applyCashback.editCashback)
                  : translate(resources.products.applyCashback.applyCashback)}
              </button>
            </div>
          </li>
        )}

        {productPaymentMethods.length > 0 &&
          productPaymentMethods.map(category => {
            const isSelected = selectedCheckoutMethod === category.budgetType;
            const categoryUi = paymentCategoriesUi(category);

            return (
              <li key={category.budgetType}>
                <div
                  className={`payment-method-box ${
                    isSelected ? 'selected' : 'not-selected'
                  }`}
                >
                  <div className={'product-payment-methods-box'}>
                    <label className="k-radio-label align-top">
                      {!isSinglePaymentMethod && (
                        <input
                          type="radio"
                          className="k-radio k-radio-md"
                          value={category.budgetType}
                          checked={isSelected}
                          onChange={() => {
                            setSelectedCheckoutMethod(category.budgetType);
                            setIsSalaryDeductionSelected(
                              category.isSalaryDeduction,
                            );
                          }}
                          aria-label={categoryUi.ariaTitle}
                        />
                      )}
                      <div className="payment-content">
                        <div className="payment-title">{categoryUi.title}</div>
                        <div className="product-payment-methods-info">
                          {categoryUi.taxesInfoComponent}
                        </div>
                      </div>
                    </label>
                    {category.error && (
                      <div className="payment-info-error">{category.error}</div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
      </ul>
      {showApplyCashbackDialog && !!customerBudget.benefitBucksBalance && (
        <ApplyCashbackDialog
          closeDialog={() => setShowApplyCashbackDialog(false)}
          applyCashback={(value: number) => {
            props.setCashbackDiscount?.(value);
          }}
          cashbackAmount={props.cashbackDiscount}
          maxCashbackAmount={Math.min(
            customerBudget.benefitBucksBalance,
            productPrice + props.cashbackDiscount,
          )}
          cashbackBudget={customerBudget.benefitBucksBalance}
          productPrice={productPrice}
        />
      )}
    </div>
  );
};
